import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  AdminGroupActivityDto,
  AdminGroupMemberDto,
  AdminGroupSubgroupDto,
  AdminGroupListItemDto,
  AdminGroupsControllerDeleteGroup200Response,
  AdminGroupsControllerEditAdminGroup200Response,
  AdminGroupsControllerGetGroupActivities200Response,
  AdminGroupsControllerGetGroupDetail200Response,
  AdminGroupsControllerGetGroupKpis200Response,
  AdminGroupsControllerGetGroupMembers200Response,
  AdminGroupsControllerGetGroupSubgroups200Response,
  AdminGroupsControllerListGroups200Response,
  AdminService,
  GetAdminGroupActivitiesResponse,
  GetAdminGroupDetailResponse,
  GetAdminGroupKpiResponse,
  GetAdminGroupMembersResponse,
  GetAdminGroupSubgroupsResponse,
  ListAdminGroupsResponse,
  ListGroupsRequestParams,
  SportDefinitionResume,
  SportsControllerGetActiveSports200Response,
  SportsService
} from '@smatchy/sdk';
import type {
  EditGroupActivity,
  EditGroupDraft,
  EditGroupMember,
  EditGroupSubgroup,
  ActivityStatusCounts,
  GroupRow,
  GroupsListQuery,
  GroupsSummary,
  PaginatedResponse,
  SportTypeConfig
} from '../../presentations/models/management.model';
import { formatActivityDateLabel, formatDate, formatDurationFromNumber, formatTimeRange24 } from '../../presentations/utils/date.util';
import { LanguageService } from '../../i18n/language.service';

export type GroupManagementDetail = {
  draft: EditGroupDraft;
  members: EditGroupMember[];
  membersTotal: number;
  subgroups: EditGroupSubgroup[];
  subgroupsTotal: number;
  activities: EditGroupActivity[];
  activitiesTotal: number;
  activityStatusCounts: ActivityStatusCounts;
};

export type GroupManagementSection<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
};

export type GroupActivityStatusTab = keyof ActivityStatusCounts;

type AdminGroupActivityApiDto = Omit<AdminGroupActivityDto, 'level'> & {
  duration?: number | null;
  level?:
    | number
    | string
    | {
        value?: number | null;
        acceptAnyLvl?: boolean | null;
      }
    | null;
  sport?: (AdminGroupActivityDto['sport'] & { icon?: string | null }) | null;
  sessionKind?: {
    id?: string | null;
    title?: string | null;
    icon?: string | null;
  } | null;
};

type AdminGroupActivityLevelApiDto =
  | AdminGroupActivityDto['level']
  | number
  | string
  | null
  | undefined;

type AdminGroupActivitiesSectionApiDto = {
  data?: AdminGroupActivityApiDto[];
  total?: number | null;
  page?: number | null;
  limit?: number | null;
  totalGroupActivities?: number | null;
  statusCounts?: {
    scheduled?: number | null;
    finished?: number | null;
    cancelled?: number | null;
    canceled?: number | null;
  } | null;
};

type AdminGroupDetailApiValue = Omit<GetAdminGroupDetailResponse, 'activities'> & {
  activities?: AdminGroupActivitiesSectionApiDto;
};

type AdminGroupDetailApiResponse = {
  error?: string | object | null;
  value?: AdminGroupDetailApiValue;
};

type AdminGroupKpiApiValue = {
  totalGroupActivities?: number | null;
};

@Injectable({ providedIn: 'root' })
export class GroupsManagementApiService {
  constructor(
    private http: HttpClient,
    private adminService: AdminService,
    private sportsService: SportsService,
    private languageService: LanguageService
  ) {}

  getSummary(): Observable<GroupsSummary> {
    return this.adminService.getGroupKpis().pipe(map(response => this.mapKpiResponse(response)));
  }

  list(query: GroupsListQuery): Observable<PaginatedResponse<GroupRow>> {
    return this.http.get<AdminGroupsControllerListGroups200Response>(
      `${this.getApiBasePath()}/api/v1/admin/groups`,
      { params: this.toListGroupsHttpParams(query) }
    ).pipe(
      map(response => this.mapListGroupsResponse(response))
    );
  }

  getSportTypes(): Observable<SportTypeConfig[]> {
    return this.sportsService.getActiveSports({ lang: this.languageService.current() }).pipe(
      map(response => this.mapSportTypesResponse(response))
    );
  }

  getDetail(groupId: string): Observable<GroupManagementDetail> {
    const params = new HttpParams()
      .set('membersLimit', 3)
      .set('subgroupsLimit', 2)
      .set('activitiesLimit', 2)
      .set('activitiesStatus', 'scheduled');

    return this.http.get<AdminGroupDetailApiResponse>(
      `${this.getApiBasePath()}/api/v1/admin/groups/${encodeURIComponent(groupId)}`,
      { params }
    ).pipe(map(response => this.mapGroupDetailResponse(response)));
  }

  getMembers(groupId: string, page = 1, limit = 10): Observable<GroupManagementSection<EditGroupMember>> {
    return this.adminService.getGroupMembers({ groupId, page, limit }).pipe(
      map(response => this.mapMembersSectionResponse(response))
    );
  }

  getSubgroups(groupId: string, page = 1, limit = 10): Observable<GroupManagementSection<EditGroupSubgroup>> {
    return this.adminService.getGroupSubgroups({ groupId, page, limit }).pipe(
      map(response => this.mapSubgroupsSectionResponse(response))
    );
  }

  getActivities(
    groupId: string,
    page = 1,
    limit = 10,
    status?: GroupActivityStatusTab
  ): Observable<GroupManagementSection<EditGroupActivity>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit);
    const statusParam = status ? this.toApiActivityStatus(status) : undefined;

    return this.http.get<AdminGroupsControllerGetGroupActivities200Response>(
      `${this.getApiBasePath()}/api/v1/admin/groups/${encodeURIComponent(groupId)}/activities`,
      { params: statusParam ? params.set('status', statusParam) : params }
    ).pipe(
      map(response => this.mapActivitiesSectionResponse(response))
    );
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.adminService.deleteGroup({ groupId }).pipe(
      map(response => this.mapDeleteGroupResponse(response))
    );
  }

  updateGroup(groupId: string, draft: EditGroupDraft, sportId?: string | null): Observable<void> {
    const url = `${this.getApiBasePath()}/api/v1/admin/groups/${encodeURIComponent(groupId)}`;

    const body = new FormData();
    body.append('name', draft.name.trim());
    body.append('description', draft.description.trim());
    body.append('websiteUrl', draft.websiteUrl.trim());

    if (sportId) {
      body.append('sportId', sportId);
    }

    if (draft.coverPhotoFile) {
      body.append('backgroundImage', draft.coverPhotoFile);
    } else if (draft.coverPhotoUrl === null) {
      body.append('backgroundImage', '');
    }

    return this.http.patch<AdminGroupsControllerEditAdminGroup200Response>(
      url,
      body
    ).pipe(
      map(response => this.mapEditGroupResponse(response))
    );
  }

  private toListGroupsHttpParams(query: GroupsListQuery): HttpParams {
    const search = query.search?.trim();
    const sportId = query.sportType && query.sportType !== 'all' ? query.sportType : undefined;
    const sortBy = this.toAdminGroupsSortBy(query.sortField);

    let params = new HttpParams()
      .set('page', query.page)
      .set('limit', query.pageSize);

    if (search) {
      params = params.set('search', search);
    }

    if (query.cityContains?.trim()) {
      params = params.set('cityContains', query.cityContains.trim());
    }

    if (query.withoutAdmins) {
      params = params.set('withoutAdmins', 'true');
    }

    if (query.privacy?.trim()) {
      params = params.set('privacy', query.privacy.trim());
    }

    if (sportId) {
      params = params.set('sportId', sportId);
    }

    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }

    if (query.sortOrder) {
      params = params.set('sortOrder', query.sortOrder);
    }

    if (query.userId?.trim()) {
      params = params.set('userId', query.userId.trim());
    }

    if (query.membership) {
      params = params.set('membership', query.membership);
    }

    return params;
  }

  private getApiBasePath(): string {
    return (this.adminService.configuration.basePath ?? '').replace(/\/$/, '');
  }

  private toAdminGroupsSortBy(sortField?: GroupsListQuery['sortField']): ListGroupsRequestParams['sortBy'] {
    switch (sortField) {
      case 'name':
        return 'name';
      case 'owner.displayName':
        return 'ownerName';
      case 'sport':
        return 'sportName';
      case 'totalSubgroups':
      case 'subgroups':
        return 'totalSubgroups';
      case 'totalMembers':
      case 'members':
        return 'totalMembers';
      case 'totalActivities':
      case 'activities':
        return 'totalActivities';
      default:
        return undefined;
    }
  }

  private mapListGroupsResponse(
    response: AdminGroupsControllerListGroups200Response | ListAdminGroupsResponse
  ): PaginatedResponse<GroupRow> {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response ? response : response.value;
    return {
      data: value?.data.map(group => this.mapGroupRow(group)) ?? [],
      total: value?.total ?? 0,
      page: value?.page ?? 1,
      pageSize: value?.limit ?? 10
    };
  }

  private mapGroupRow(group: AdminGroupListItemDto): GroupRow {
    const sportIconSrc = this.toSportIconSource(group.sport?.icon);
    const ownerDisplayName = group.owner?.displayName ?? '';
    const sportSnap = group.sport as (typeof group.sport & { nameFr?: string; nameEn?: string });
    const french = this.languageService.current() === 'fr';
    const localizedSportName =
      (french ? sportSnap?.nameFr || sportSnap?.nameEn : sportSnap?.nameEn || sportSnap?.nameFr) ||
      group.sport?.name ||
      '';

    return {
      id: group.id,
      name: group.name,
      admin: {
        name: ownerDisplayName || '-',
        email: group.owner?.email ?? ''
      },
      owner: group.owner ? {
        id: group.owner.id,
        displayName: group.owner.displayName,
        email: group.owner.email,
        avatarUrl: group.owner.avatarUrl
      } : undefined,
      sport: group.sport ? {
        id: group.sport.id,
        name: localizedSportName,
        nameFr: sportSnap?.nameFr,
        nameEn: sportSnap?.nameEn,
        icon: group.sport.icon,
        iconSrc: sportIconSrc
      } : null,
      sportName: localizedSportName,
      totalSubgroups: group.totalSubgroups ?? 0,
      totalMembers: group.totalMembers ?? 0,
      totalActivities: group.totalActivities ?? 0,
      locationName: group.locationName ?? '',
      adminsCount: (group as { adminsCount?: number }).adminsCount ?? 0,
      privacy: (group as { privacy?: string }).privacy,
      subgroups: group.totalSubgroups ?? 0,
      members: group.totalMembers ?? 0,
      activities: group.totalActivities ?? 0,
      createdOn: ''
    };
  }

  private toSportIconSource(icon?: string): string | undefined {
    const trimmedIcon = icon?.trim();
    if (!trimmedIcon) {
      return undefined;
    }

    if (
      trimmedIcon.startsWith('http://') ||
      trimmedIcon.startsWith('https://') ||
      trimmedIcon.startsWith('data:') ||
      trimmedIcon.startsWith('assets/') ||
      trimmedIcon.startsWith('/assets/')
    ) {
      return trimmedIcon;
    }

    if (trimmedIcon.includes('/')) {
      return trimmedIcon;
    }

    return trimmedIcon.includes('.')
      ? `assets/icons/sport/${trimmedIcon}`
      : `assets/icons/sport/${trimmedIcon}.svg`;
  }

  private mapSportTypesResponse(response: SportsControllerGetActiveSports200Response): SportTypeConfig[] {
    if (response.error) {
      throw response.error;
    }

    return (response.value?.sports ?? [])
      .map(sport => this.mapSportTypeConfig(sport))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private mapSportTypeConfig(sport: SportDefinitionResume): SportTypeConfig {
    return {
      label: sport.name,
      value: sport._id || sport.id,
      icon: sport.icon || sport.id,
      maxLevel: sport.maxLevel
    };
  }

  private mapGroupDetailResponse(
    response: AdminGroupsControllerGetGroupDetail200Response | GetAdminGroupDetailResponse | AdminGroupDetailApiResponse
  ): GroupManagementDetail {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'id' in response ? response : response.value;
    if (!value) {
      throw new Error('Group detail response is empty.');
    }

    const sportName = value.sport?.name ?? '';
    const activitiesSection = value.activities as AdminGroupActivitiesSectionApiDto | undefined;

    return {
      draft: {
        id: value.id,
        name: value.name,
        createdOn: formatDate(value.createdAt ?? ''),
        coverPhotoUrl: value.backgroundUrl ?? null,
        description: value.description ?? '',
        websiteUrl: value.websiteUrl ?? '',
        sport: sportName,
        sportIcon: value.sport?.icon
      },
      members: value.members?.data.map(member => this.mapDetailMember(member)) ?? [],
      membersTotal: value.members?.total ?? 0,
      subgroups: value.subgroups?.data.map(subgroup => this.mapDetailSubgroup(subgroup)) ?? [],
      subgroupsTotal: value.subgroups?.total ?? 0,
      activities: activitiesSection?.data?.map(activity => this.mapDetailActivity(activity)) ?? [],
      activitiesTotal: activitiesSection?.totalGroupActivities ?? activitiesSection?.total ?? 0,
      activityStatusCounts: this.mapActivityStatusCounts(activitiesSection)
    };
  }

  private mapActivityStatusCounts(activitiesSection?: AdminGroupActivitiesSectionApiDto): ActivityStatusCounts {
    const statusCounts = activitiesSection?.statusCounts;
    if (statusCounts) {
      return {
        incoming: statusCounts.scheduled ?? 0,
        completed: statusCounts.finished ?? 0,
        cancelled: statusCounts.cancelled ?? statusCounts.canceled ?? 0
      };
    }

    const counts: ActivityStatusCounts = {
      incoming: 0,
      completed: 0,
      cancelled: 0
    };

    for (const activity of activitiesSection?.data ?? []) {
      const status = this.toActivityStatusTab(activity.status);
      counts[status] += 1;
    }

    return counts;
  }

  private toActivityStatusTab(status?: string): keyof ActivityStatusCounts {
    const normalizedStatus = status?.trim().toLowerCase();
    switch (normalizedStatus) {
      case 'scheduled':
        return 'incoming';
      case 'finished':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'incoming';
    }
  }

  private toApiActivityStatus(status: GroupActivityStatusTab): string {
    switch (status) {
      case 'completed':
        return 'finished';
      case 'cancelled':
        return 'cancelled';
      case 'incoming':
      default:
        return 'scheduled';
    }
  }

  private mapMembersSectionResponse(
    response: AdminGroupsControllerGetGroupMembers200Response | GetAdminGroupMembersResponse
  ): GroupManagementSection<EditGroupMember> {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response ? response : response.value;
    return {
      data: value?.data.map(member => this.mapDetailMember(member)) ?? [],
      total: value?.total ?? 0,
      page: value?.page ?? 1,
      limit: value?.limit ?? 10
    };
  }

  private mapSubgroupsSectionResponse(
    response: AdminGroupsControllerGetGroupSubgroups200Response | GetAdminGroupSubgroupsResponse
  ): GroupManagementSection<EditGroupSubgroup> {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response ? response : response.value;
    return {
      data: value?.data.map(subgroup => this.mapDetailSubgroup(subgroup)) ?? [],
      total: value?.total ?? 0,
      page: value?.page ?? 1,
      limit: value?.limit ?? 10
    };
  }

  private mapActivitiesSectionResponse(
    response: AdminGroupsControllerGetGroupActivities200Response | GetAdminGroupActivitiesResponse
  ): GroupManagementSection<EditGroupActivity> {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'data' in response ? response : response.value;
    return {
      data: value?.data.map(activity => this.mapDetailActivity(activity)) ?? [],
      total: value?.total ?? 0,
      page: value?.page ?? 1,
      limit: value?.limit ?? 10
    };
  }

  private mapDeleteGroupResponse(response: AdminGroupsControllerDeleteGroup200Response): void {
    if (response.error) {
      throw response.error;
    }
  }

  private mapEditGroupResponse(response: AdminGroupsControllerEditAdminGroup200Response): void {
    if (response.error) {
      throw response.error;
    }
  }

  private mapDetailMember(member: AdminGroupMemberDto): EditGroupMember {
    return {
      name: member.displayName,
      email: member.email,
      avatarUrl: member.avatarUrl,
      role: member.roleName === 'Member' ? undefined : member.roleName
    };
  }

  private mapDetailSubgroup(subgroup: AdminGroupSubgroupDto): EditGroupSubgroup {
    const sportName = subgroup.sport?.name ?? '';

    return {
      id: subgroup.id,
      name: subgroup.name,
      members: subgroup.totalMembersCount ?? 0,
      activities: subgroup.nestedSubgroupCount ?? 0,
      thumbnailUrl: subgroup.backgroundUrl ?? '',
      sport: sportName,
      sportIcon: subgroup.sport?.icon ?? '',
      location: this.formatLocation(subgroup.location),
      admin: '',
      description: '',
      websiteUrl: ''
    };
  }

  private mapDetailActivity(activity: AdminGroupActivityDto | AdminGroupActivityApiDto): EditGroupActivity {
    const apiActivity = activity as AdminGroupActivityApiDto & { privacy?: 'public' | 'private' | 'members_only' };
    const sportName = apiActivity.sport?.name ?? '';
    const sessionKindTitle = apiActivity.sessionKind?.title?.trim() ?? '';
    const durationLabel = formatDurationFromNumber(apiActivity.duration);
    const activityTypeLabel = [sessionKindTitle || activity.type || '', durationLabel].filter(Boolean).join(' \u2022 ');

    return {
      id: activity.id,
      name: activity.title,
      time: formatTimeRange24(activity.startDate, activity.endDate),
      date: formatActivityDateLabel(activity.startDate),
      status: activity.status ?? '',
      thumbnailUrl: activity.backgroundUrl ?? '',
      sport: sportName,
      location: activity.locationName ?? '',
      host: '',
      participants: 0,
      activityType: activityTypeLabel,
      sportIcon: apiActivity.sport?.icon ?? '',
      sessionKindIcon: apiActivity.sessionKind?.icon ?? '',
      durationSeconds: typeof apiActivity.duration === 'number' ? apiActivity.duration : undefined,
      level: this.mapActivityLevel(apiActivity.level),
      description: '',
      privacy: apiActivity.privacy
    };
  }


  private mapActivityLevel(level: AdminGroupActivityApiDto['level']): string {
    if (level && typeof level === 'object') {
      if (level.acceptAnyLvl) {
        return 'All levels';
      }

      return level.value == null ? '' : String(level.value);
    }

    if (level == null) {
      return '';
    }

    const normalizedLevel = String(level).trim();
    if (normalizedLevel.toLowerCase() === 'all' || normalizedLevel.toLowerCase() === 'all levels') {
      return 'All levels';
    }

    return normalizedLevel.match(/\d+/)?.[0] ?? normalizedLevel;
  }

  private formatLocation(location?: object): string {
    if (!location) {
      return '';
    }

    const maybeLocation = location as { name?: string; address?: string; city?: string };
    return maybeLocation.name ?? maybeLocation.address ?? maybeLocation.city ?? '';
  }

  private mapKpiResponse(response: AdminGroupsControllerGetGroupKpis200Response | GetAdminGroupKpiResponse): GroupsSummary {
    if ('error' in response && response.error) {
      throw response.error;
    }

    const value = 'totalGroups' in response ? response : response.value;
    const kpiValue = value as typeof value & AdminGroupKpiApiValue;
    const topSport = value?.topSport ?? null;
    const ts = topSport as (typeof topSport & { nameFr?: string; nameEn?: string });
    const french = this.languageService.current() === 'fr';
    const topSportType =
      (french ? ts?.nameFr || ts?.nameEn : ts?.nameEn || ts?.nameFr) || ts?.name || '-';

    return {
      totalGroups: value?.totalGroups ?? 0,
      groupsWithoutAdmins: (value as { groupsWithoutAdmins?: number })?.groupsWithoutAdmins ?? 0,
      groupsGrowth: value?.groupsCreatedThisMonth ?? 0,
      totalMembers: value?.totalMembers ?? 0,
      totalActivities: kpiValue?.totalGroupActivities ?? 0,
      topSportType,
      topSportPercentage: topSport?.percentage ?? 0
    };
  }
}
